"use client"

import { useEffect, useRef } from 'react'
import { useNotificationStore } from '@/stores/notification-store'
import { useAuthStore } from '@/stores'
import { convex } from '@/lib/convex-client'
import { api } from '@/convex/_generated/api'
import type { NotificationType } from '@/types/notification'

/**
 * Hook that initializes the notification system and provides a `notify` function.
 *
 * Usage:
 * ```tsx
 * const { notify, unreadCount, requestPermission } = useNotifications()
 * notify({ type: 'timer_complete', title: "Time's up!", message: '...' })
 * ```
 */
export function useNotifications() {
  const user = useAuthStore((s) => s.user)
  const initialized = useRef(false)

  const notify = useNotificationStore((s) => s.notify)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const browserPermission = useNotificationStore((s) => s.browserPermission)
  const preferences = useNotificationStore((s) => s.preferences)
  const requestPermission = useNotificationStore((s) => s.requestPermission)
  const refreshPermissionState = useNotificationStore((s) => s.refreshPermissionState)
  const fetchPreferences = useNotificationStore((s) => s.fetchPreferences)

  // Initialize on auth
  useEffect(() => {
    if (!user || initialized.current) return
    initialized.current = true

    fetchPreferences()
  }, [user, fetchPreferences])

  // The permission belongs to the browser and can be changed from its settings
  // while the tab is open, so it is watched for the whole session rather than
  // read once at start-up.
  useEffect(() => {
    refreshPermissionState()
    return useNotificationStore.getState().watchPermissionState()
  }, [refreshPermissionState])

  // The producer formats reminders in the user's own timezone, so it needs the
  // browser's — re-synced on focus because a laptop can move between runs.
  useEffect(() => {
    if (!user || typeof window === 'undefined') return

    const syncTimezone = async () => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (!timezone) return

      try {
        await convex.mutation(api.users.setPreferences, { timezone })
      } catch (error) {
        console.error('Error syncing notification timezone:', error)
      }
    }

    void syncTimezone()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncTimezone()
      }
    }

    window.addEventListener('focus', syncTimezone)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', syncTimezone)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user])

  // Live inbox. Reminders are produced by a Convex cron into the same table
  // this subscribes to, so they arrive as soon as they are written — the
  // 60-second poll that bridged the two databases is gone.
  useEffect(() => {
    if (!user) return
    return useNotificationStore.getState().subscribeToInbox()
  }, [user])

  // Reset on logout
  useEffect(() => {
    if (!user) {
      initialized.current = false
    }
  }, [user])

  return {
    notify,
    unreadCount,
    browserPermission,
    preferences,
    requestPermission,
  }
}

/**
 * Standalone function to send a notification from outside React components
 * (e.g., from Zustand stores or utility functions).
 */
export function notifyFromStore(options: {
  type: NotificationType
  title: string
  message: string
  taskId?: string
  tag?: string
  onClick?: () => void
}): void {
  useNotificationStore.getState().notify(options)
}
